import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormArray, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { Integration, PlatformUserMapping, UserMapping } from 'voxloud-types/dist/models';

@Component({
  selector: 'gdp-integration-user-mapping',
  templateUrl: './integration-user-mapping.component.html',
  styleUrls: ['./integration-user-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntegrationUserMappingComponent),
      multi: true,
    },
  ],
})
export class IntegrationUserMappingComponent implements ControlValueAccessor, OnInit {
  private onChange: (_) => any;
  private onTouch: (_) => any;
  @Input() allAvailableUsers;
  @Input() connection: Integration;
  public form = new FormArray([]);

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.cdr.markForCheck();
  }

   public get availableUsers() {
    if (this.form) {
      const currentMappingIds: string[] = this.form.value
        .map((m: {user: UserMapping}) => m.user?.id);
      return this.allAvailableUsers?.availableUsers
        .filter(u => !currentMappingIds.includes(u.aggregate_id))
        .map(u => ({ id: u.aggregate_id, first_name: u.first_name, last_name: u.last_name, email: u.email }));
    } else {
      return [];
    }
  }

  public get availableCrmUsers() {
    if (this.form) {
      const currentMappingIds: string[] = this.form.value
        .map((m: {platform_user: PlatformUserMapping}) => m.platform_user?.external_id);
      return this.allAvailableUsers?.availableCrmUsers
        .filter(u => !currentMappingIds.includes(u.external_id))
        .map(u => ({ external_id: u.external_id, first_name: u.first_name, last_name: u.last_name, email: u.email }));
    } else {
      return [];
    }
  }

  writeValue(value): void {
    value.forEach(m => {
      const group = new FormGroup({
        user: new FormControl(m.user, [Validators.required]),
        platform_user: new FormControl(m.platform_user, [Validators.required])
      });
      this.form.push(group);
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public selectUser(user: UserMapping, i:number) {
    this.form.at(i).patchValue({ user });
    this.change();
  }

  public selectCrmUser(platform_user: PlatformUserMapping, i:number) {
    this.form.at(i).patchValue({ platform_user });
    this.change();
  }

  public addMapping() {
    const availableUser = {...this.availableUsers[0]};
    const availableCrmUser = {...this.availableCrmUsers[0]};
    this.form.push(
      new FormGroup({
        user: new FormControl(
          {
            id: availableUser.id,
            first_name: availableUser.first_name,
            last_name: availableUser.last_name,
            email: availableUser.email
          },
          [Validators.required]
        ),
        platform_user: new FormControl(
          {
            external_id: availableCrmUser.external_id,
            first_name: availableCrmUser.first_name,
            last_name: availableCrmUser.last_name,
            email: availableCrmUser.email
          },
          [Validators.required]
        )
      })
    );
    this.change();
  }

  public removeMapping(i: number) {
    this.form.removeAt(i);
    this.change();
  }


  private change() {
    this.cdr.markForCheck();
    if (this.form.value) {
      const value = this.form.value;
      this.onChange(value);
    } else {
      this.onChange([]);
    }
  }
  
}
